/**
 * Acknowledge Flagged Break Modal (PR 1, Phase B1 · BREAK-ACK-NOTE-001 · IT-BREAK-ACK-001 premium shell)
 *
 * Admin/TL/IT-Manager action — mark a flagged break as reviewed. The acknowledgement
 * note is MANDATORY (non-blank, ≤ 500 chars) and write-once: for already
 * acknowledged breaks the modal renders a read-only audit view instead.
 *
 * Backend contract: POST /api/breaks/{id}/acknowledge
 *   Body: { note: string }  → 409 when already acknowledged
 *   Response: { ok: true, break: <updated_row> }
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { Check, Loader2, Lock, X, Coffee, AlertTriangle, CalendarDays, UserRound, FileText, ShieldCheck, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { formatDateTimeShortET } from './breakUiHelpers';
import './AcknowledgeBreakModal.css';

const API = process.env.REACT_APP_BACKEND_URL || '';
const MAX_NOTE = 500;

const initials = (name) => (name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
const roleLabel = (r) => (r ? String(r).replace(/_/g, ' ') : '');

const Row = ({ k, v, testId, strong, children }) => (
  <>
    <dt>{k}</dt>
    <dd className={strong ? 'strong' : ''} data-testid={testId}>{children ?? v}</dd>
  </>
);

/* Shared header + summary strip for both the read-only audit view and the acknowledge form */
const Shell = ({ breakRecord, acked, onClose, children }) => {
  const ack = breakRecord.admin_acknowledgement || {};
  const type = ack.break_type_name || breakRecord.break_type_name || 'Break';
  const name = ack.employee_name || breakRecord.user_name || '—';
  const reason = ack.flagged_reason || breakRecord.flagged_reason;
  return (
    <>
      <header className="abm-header">
        <span className={`abm-badge ${acked ? 'ok' : 'pending'}`} aria-hidden>
          {acked ? <Check className="w-6 h-6" strokeWidth={3} /> : <ShieldCheck className="w-6 h-6" />}
        </span>
        <div className="min-w-0">
          <DialogTitle className="abm-title">{acked ? 'Break Acknowledged' : 'Acknowledge Flagged Break'}</DialogTitle>
          <DialogDescription className="abm-subtitle">
            {acked ? 'Acknowledgement details for a flagged break' : 'Document why this exception is accepted'}
          </DialogDescription>
        </div>
        <button type="button" className="abm-close" onClick={onClose} aria-label="Close" data-testid="ack-close-x"><X className="w-4 h-4" /></button>
        <svg className="abm-wave" viewBox="0 0 600 40" preserveAspectRatio="none" aria-hidden><path d="M0 40 C120 10 260 46 400 18 C480 2 550 8 600 24 L600 40 Z" /></svg>
      </header>

      <div className="abm-body">
        <div className="abm-strip" data-testid="ack-summary">
          <span className="abm-avatar">{initials(name)}</span>
          <div><b>{name}</b><small>Employee</small></div>
          <i className="abm-sep" />
          <span className="abm-type"><Coffee className="w-4 h-4" /></span>
          <div><b>{type}</b><small>Break Type</small></div>
          {breakRecord.flagged && <span className="abm-flag" data-testid="ack-flag-pill">Flagged</span>}
        </div>
        {reason && (
          <div className="abm-reason" data-testid="ack-flag-reason">
            <AlertTriangle className="w-4 h-4 shrink-0" /> <span>Flag Reason: <b>{reason}</b></span>
          </div>
        )}
        {children}
      </div>
    </>
  );
};

const BreakDetails = ({ breakRecord }) => {
  const ack = breakRecord.admin_acknowledgement || {};
  const dur = ack.duration_minutes ?? breakRecord.duration_minutes;
  const cap = ack.max_duration_minutes ?? breakRecord.max_duration_minutes;
  const excess = dur != null && cap != null ? dur - cap : null;
  const type = ack.break_type_name || breakRecord.break_type_name || 'Break';
  return (
    <section className="abm-card" data-testid="ack-break-details">
      <h4><CalendarDays className="w-4 h-4" /> Break Details</h4>
      <dl>
        <Row k="Break Start" v={formatDateTimeShortET(ack.start_time || breakRecord.start_time)} />
        <Row k="Break End" v={formatDateTimeShortET(breakRecord.end_time) || '—'} />
        <Row k="Actual Duration" v={dur != null ? `${dur} min` : '—'} strong />
        <Row k="Allowed Duration" v={cap != null ? `${cap} min (${type} cap)` : '—'} />
        <Row k="Excess Time">
          {excess != null && excess > 0
            ? <span className="abm-excess" data-testid="ack-excess">+{excess} min</span>
            : <span className="abm-muted">{excess != null ? 'within limit' : '—'}</span>}
        </Row>
      </dl>
    </section>
  );
};

const AckAuditView = ({ breakRecord }) => {
  const ack = breakRecord.admin_acknowledgement || {};
  const by = ack.acknowledged_by_name || breakRecord.admin_acknowledged_by_name || '—';
  const role = roleLabel(ack.acknowledged_by_role);
  const dur = ack.duration_minutes ?? breakRecord.duration_minutes;
  const cap = ack.max_duration_minutes ?? breakRecord.max_duration_minutes;
  return (
    <div className="abm-grid" data-testid="ack-audit-view">
      <BreakDetails breakRecord={breakRecord} />
      <section className="abm-card" data-testid="ack-ack-details">
        <h4><UserRound className="w-4 h-4" /> Acknowledgement Details</h4>
        <ul className="abm-list">
          <li><UserRound className="w-4 h-4" /><span>Acknowledged by</span><b data-testid="ack-audit-acknowledged-by">{by}{role ? ` (${role})` : ''}</b></li>
          <li><Clock className="w-4 h-4" /><span>Acknowledged at</span><b data-testid="ack-audit-acknowledged-at">{formatDateTimeShortET(ack.acknowledged_at || breakRecord.admin_acknowledged_at)}</b></li>
        </ul>
        {/* legacy test hooks (values unchanged) */}
        <span className="sr-only" data-testid="ack-audit-employee">{ack.employee_name || breakRecord.user_name || '—'}</span>
        <span className="sr-only" data-testid="ack-audit-break">{`${ack.break_type_name || breakRecord.break_type_name || '—'} · ${formatDateTimeShortET(ack.start_time || breakRecord.start_time)}`}</span>
        <span className="sr-only" data-testid="ack-audit-duration">{`${dur ?? '—'} min${cap ? ` / ${cap} min cap` : ''}`}</span>
        <span className="sr-only" data-testid="ack-audit-original-flag-reason">{ack.flagged_reason || breakRecord.flagged_reason || '—'}</span>
      </section>
      <section className="abm-card abm-note-card" data-testid="ack-note-card">
        <h4><FileText className="w-4 h-4" /> Acknowledgement Note</h4>
        <div className="abm-note" data-testid="ack-audit-note">{ack.note || breakRecord.admin_note || '—'}</div>
        <p className="abm-lock" data-testid="ack-audit-locked"><Lock className="w-3 h-3" /> Acknowledgement notes are write-once and cannot be edited.</p>
      </section>
    </div>
  );
};

export const AcknowledgeBreakModal = ({ open, onOpenChange, breakRecord, onSuccess }) => {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => { setNote(''); }, [breakRecord]);

  const trimmed = note.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= MAX_NOTE && !submitting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(`${API}/api/breaks/${breakRecord.id}/acknowledge`, { note: trimmed }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Break acknowledged.', { description: `Reviewed by ${resp.data.break?.admin_acknowledged_by_name || 'you'}` });
      onOpenChange(false);
      onSuccess?.(resp.data.break);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error('Failed to acknowledge break', { description: typeof detail === 'string' ? detail : (detail?.[0]?.msg || err?.message) });
    } finally {
      setSubmitting(false);
    }
  };

  if (!breakRecord) return null;
  const isAcked = !!breakRecord.admin_acknowledged;
  const close = () => !submitting && onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent hideCloseButton className="abm-dialog" overlayClassName="abm-overlay" data-testid="ack-modal">
        <Shell breakRecord={breakRecord} acked={isAcked} onClose={close}>
          {isAcked ? <AckAuditView breakRecord={breakRecord} /> : (
            <div className="abm-grid abm-grid-form">
              <BreakDetails breakRecord={breakRecord} />
              <section className="abm-card abm-note-card abm-form-card">
                <h4><FileText className="w-4 h-4" /> <Label htmlFor="ack-note" className="abm-form-label">Acknowledgement Note <span className="text-rose-400" aria-hidden="true">*</span></Label></h4>
                <Textarea id="ack-note" data-testid="ack-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="e.g. Confirmed with employee — extended due to client call" maxLength={MAX_NOTE} rows={4}
                  className="abm-textarea" disabled={submitting} required aria-required="true" />
                <p className="abm-hint" data-testid="ack-note-hint">
                  Required — stored with your name and timestamp for audit, write-once. <span>{note.length}/{MAX_NOTE}</span>
                </p>
              </section>
            </div>
          )}
          <footer className="abm-footer">
            <Button variant="outline" onClick={close} disabled={submitting} className={isAcked ? 'abm-btn-primary' : 'abm-btn-ghost'} data-testid="ack-cancel">
              {isAcked ? 'Close' : 'Cancel'}
            </Button>
            {!isAcked && (
              <Button onClick={handleConfirm} disabled={!canSubmit} title={canSubmit ? undefined : 'Enter an acknowledgement note to continue'}
                className="abm-btn-confirm" data-testid="ack-confirm">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : <><Check className="w-4 h-4 mr-1.5" strokeWidth={3} /> Acknowledge</>}
              </Button>
            )}
          </footer>
        </Shell>
      </DialogContent>
    </Dialog>
  );
};

export default AcknowledgeBreakModal;
